import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CurrentUserService } from '@core/account-logic';
import type { CurrentUser } from '@core/account-types';
import { I18nService } from '@core/i18n-legacy';
import { type NoteAvatarAuthor, userToMention } from '@core/notes-legacy';
import type { Note, NoteLocalStorageKeyBuilder } from '@core/notes-types';
import type { MentionElement } from '@core/prosemirror-legacy';
import { VisibilitySelectorContainerComponent } from '@core/visibility-legacy';

@Injectable({ providedIn: 'root' })
export class RecruiteeNotesService {
  public visibilitySelectorComponent = VisibilitySelectorContainerComponent;

  private i18n = inject(I18nService);
  private currentUser: CurrentUser;

  constructor() {
    inject(CurrentUserService)
      .currentUser$.pipe(takeUntilDestroyed())
      .subscribe(currentUser => {
        this.currentUser = currentUser;
      });
  }

  public isOwnerFn = (note: Note) => !!this.currentUser && note.adminId === this.currentUser.id;

  public getOwnerFn = (note: Note): NoteAvatarAuthor | null => {
    if (note.admin) {
      return {
        name: `${note.admin.firstName} ${note.admin.lastName}`,
        photoThumbUrl: note.admin.photoThumbUrl,
        hasAvatar: note.admin.hasAvatar,
        initials: note.admin.initials,
      };
    } else if (note.guest) {
      return {
        name: note.guest.name,
        photoThumbUrl: note.guest.photoThumbUrl,
        hasAvatar: note.guest.hasAvatar,
        initials: note.guest.initials,
        isGuest: true,
      };
    } else if (note.triggered) {
      return null;
    } else {
      return {
        name: this.i18n.translate('app.common.deleted_user'),
        photoThumbUrl: '',
        hasAvatar: false,
        initials: '',
      };
    }
  };
  public localStorageKeyBuilderFactory =
    (context: { candidateId?: number; offerId?: number }): NoteLocalStorageKeyBuilder<Note> =>
    (params: { parentNote: Note }): string =>
      `${context.candidateId}-${context.offerId}-${params.parentNote.id}`;

  public noteToMention = (note: Note): MentionElement | null =>
    note.admin ? userToMention(note.admin) : null;
  public isAutomatedActionNoteFn = (note: Note): boolean => !note.adminId && !note.guestId;
}
